/**
 * SmartImprove Implementation tab — v4 (Feb 2026 refactor).
 *
 * Implementation planning fully reuses the SmartEvaluate-style 4-section
 * planner: Phases · Resources · Weekly Effort Matrix · Cost Model. There is
 * NO warehouse-rollout / wave assignment in SmartImprove scope. One plan
 * per accepted recommendation; the Edit dialog opens the planner for that
 * single plan.
 *
 * Templates:
 *   • Object Matrix Template  → normal operational recs (Build/Test/Deploy)
 *   • Big Bang Program        → strategic recs (single coordinated cutover)
 *   • Pilot + Rollout Program → strategic recs (phased)
 *
 * The previous standalone WRICEF planner UI has been removed. WRICEF
 * survives as BACKGROUND effort-seeding logic driven by each
 * recommendation's auto-classified Type + Complexity (set on the
 * Recommendations tab).
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Workflow,
  CheckCircle2,
  Archive,
  Pencil,
  Eye,
  Calendar,
  Clock,
  Users,
  DollarSign,
  Info,
} from "lucide-react";
import { Button } from "../../../components/ui/button";
import { Input } from "../../../components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../../components/ui/select";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "../../../components/ui/tooltip";
import { OverlapWarning } from "../OverlapWarning";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const ax = axios.create({
  baseURL: `${BACKEND_URL}/api/smartimprove`,
  withCredentials: true,
});

const STATUS_CHIP = {
  draft: "bg-[#F3F4F6] text-[#374151] border-[#E5E7EB]",
  finalized: "bg-[#ECFDF5] text-[#047857] border-[#A7F3D0]",
  archived: "bg-[#FEE2E2] text-[#991B1B] border-[#FECACA]",
};

const COMMON_CURRENCIES = ["USD", "EUR", "GBP", "INR", "AUD", "CAD", "SGD", "AED", "JPY"];

const COST_MODEL_KEYS = [
  { key: "hardware_usd", label: "Hardware / Devices" },
  { key: "integration_usd", label: "Integration / Custom Dev" },
  { key: "internal_business_effort_usd", label: "Internal Business Effort" },
  { key: "ams_annual_usd", label: "AMS (Annual)" },
  { key: "recurring_support_annual_usd", label: "Recurring Support (Annual)" },
];

const fmtMoney = (amount, currency = "USD") => {
  const n = Number(amount) || 0;
  return `${currency} ${n.toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
};

const convert = (amountUsd, fxRate, discountPct) => {
  const converted = (Number(amountUsd) || 0) * (Number(fxRate) || 1);
  const factor = 1 - Math.max(0, Number(discountPct) || 0) / 100;
  return converted * factor;
};

const phaseTotalWeeks = (phases) =>
  (phases || []).reduce((a, p) => a + (Number(p.duration_weeks) || 0), 0);

const matrixTotalHours = (matrix) => {
  let total = 0;
  Object.values(matrix || {}).forEach((row) => {
    Object.values(row || {}).forEach((h) => {
      total += Number(h) || 0;
    });
  });
  return total;
};

const resourceBlendedRate = (r) => {
  const delivery = (r.delivery_type || "offshore").toLowerCase();
  return delivery === "onsite"
    ? Number(r.onsite_rate_usd_per_hr) || 0
    : Number(r.offshore_rate_usd_per_hr) || 0;
};

const computeServicesCost = (matrix, resources) => {
  const rateByRole = {};
  (resources || []).forEach((r) => {
    rateByRole[r.role || "Resource"] = resourceBlendedRate(r);
  });
  let total = 0;
  Object.entries(matrix || {}).forEach(([, row]) => {
    Object.entries(row || {}).forEach(([role, hours]) => {
      total += (Number(hours) || 0) * (rateByRole[role] || 0);
    });
  });
  return total;
};

const computeTotalOneTime = (services, cm) => {
  const c = cm || {};
  return (
    services +
    (Number(c.hardware_usd) || 0) +
    (Number(c.integration_usd) || 0) +
    (Number(c.internal_business_effort_usd) || 0)
  );
};

const computeAnnualRecurring = (cm) => {
  const c = cm || {};
  return (Number(c.ams_annual_usd) || 0) + (Number(c.recurring_support_annual_usd) || 0);
};

export default function ImplementationTab({ engagementId, isFinalized = false }) {
  const [plans, setPlans] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [currency, setCurrency] = useState({
    base_currency: "USD",
    display_currency: "USD",
    fx_rate: 1,
    discount_pct: 0,
  });
  const [loading, setLoading] = useState(true);
  const [dialog, setDialog] = useState(null); // {plan, readOnly}
  const [showDrafts, setShowDrafts] = useState(false);

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const [p, c] = await Promise.all([
        ax.get(`/engagements/${engagementId}/implementation-plans`),
        ax.get(`/engagements/${engagementId}/implementation/currency`),
      ]);
      setPlans(p.data || []);
      setCurrency(c.data || currency);
    } catch {
      toast.error("Failed to load implementation plans");
    }
    try {
      const t = await ax.get("/implementation-templates");
      setTemplates(t.data || []);
    } catch {
      // Templates are only needed for the edit dialog — non-fatal.
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [engagementId]);

  useEffect(() => {
    reload();
  }, [reload]);

  const saveCurrency = useCallback(
    async (patch) => {
      const next = { ...currency, ...patch };
      setCurrency(next);
      try {
        await ax.put(`/engagements/${engagementId}/implementation/currency`, {
          display_currency: next.display_currency,
          fx_rate: Number(next.fx_rate) || 1,
          discount_pct: Number(next.discount_pct) || 0,
        });
        // Notify the ROI tab (same session) to re-fetch with the new currency.
        window.dispatchEvent(new CustomEvent("smartimprove:currency-changed", {
          detail: { engagementId },
        }));
      } catch (e) {
        const detail = e?.response?.data?.detail;
        toast.error(typeof detail === "string" ? detail : "Failed to save currency");
      }
    },
    [currency, engagementId]
  );

  const finalizedPlans = useMemo(
    () => plans.filter((p) => p.status === "finalized"),
    [plans]
  );

  const totals = useMemo(() => {
    if (!finalizedPlans.length) return null;
    let totalWeeks = 0;
    let totalHours = 0;
    let totalCostUsd = 0;
    let totalRecurringUsd = 0;
    let peakResources = 0;
    finalizedPlans.forEach((p) => {
      totalWeeks += Math.ceil(Number(p.estimated_weeks) || 0);
      totalCostUsd += Number(p.total_cost_usd) || 0;
      totalRecurringUsd += Number(p.total_annual_recurring_usd) || 0;
      totalHours += Number(p.total_effort_hours) || 0;
      const planResources = (p.resources || []).filter(
        (r) => r && (r.role || "").toString().trim() !== "",
      ).length;
      if (planResources > peakResources) peakResources = planResources;
    });
    return {
      totalWeeks,
      totalHours,
      totalCostUsd,
      totalRecurringUsd,
      peakResources,
    };
  }, [finalizedPlans]);

  if (loading) {
    return <div className="text-sm text-[#6B7280]">Loading implementation plans…</div>;
  }

  // Full-page inline editor takes over the tab when a plan is being edited or
  // viewed. Replaces the previous modal popup per the latest UX mandate.
  if (dialog) {
    return (
      <PlanEditor
        plan={dialog.plan}
        readOnly={dialog.readOnly}
        templates={templates}
        onClose={() => setDialog(null)}
        onSaved={async () => {
          setDialog(null);
          await reload();
        }}
      />
    );
  }

  if (!plans.length) {
    return (
      <EmptyState
        title="No accepted recommendations yet"
        description="Accept recommendations to generate implementation plans."
      />
    );
  }

  return (
    <div className="space-y-5 max-w-7xl mx-auto" data-testid="smartimprove-implementation-tab">
      <OverlapWarning
        engagementId={engagementId}
        scope="implementation"
        title="Coverage overlap detected"
        description="Some recommendations in the implementation portfolio address the same findings. Review the portfolio before finalizing the business case."
      />
      <header className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <p className="text-[11px] font-semibold tracking-[0.18em] uppercase text-[#025979]">
            Implementation Planning
          </p>
          <h2
            className="text-xl font-bold text-[#111827] mt-0.5"
            style={{ fontFamily: "Chivo, sans-serif" }}
          >
            One plan per accepted recommendation
          </h2>
          <p className="text-xs text-[#6B7280] mt-1 max-w-2xl">
            Phases, Resources, Weekly Effort Matrix and Cost Model — reusing the
            SmartEvaluate planner. Object Matrix seeds default effort from each
            recommendation's Type + Complexity (WRICEF background logic).
          </p>
        </div>
        <CurrencyConverter
          currency={currency}
          onChange={saveCurrency}
          disabled={isFinalized}
        />
      </header>

      <SummaryCards totals={totals} currency={currency} planCount={finalizedPlans.length} />

      {finalizedPlans.length === 0 ? (
        <div
          className="bg-[#FFF7ED] border border-[#FED7AA] text-[#9A3412] text-xs rounded-sm px-4 py-2.5"
          data-testid="impl-no-finalized-banner"
        >
          No finalized implementation plans yet. Finalize plans below to populate
          the summary KPIs and feed ROI.
        </div>
      ) : null}

      <SummaryTable
        plans={plans}
        finalizedPlans={finalizedPlans}
        currency={currency}
        showDrafts={showDrafts}
        onToggleShowDrafts={setShowDrafts}
      />

      <section className="grid grid-cols-1 lg:grid-cols-2 gap-4" data-testid="impl-plan-cards">
        {plans.map((plan) => (
          <PlanCard
            key={plan.id}
            plan={plan}
            currency={currency}
            isFinalized={isFinalized}
            onView={() => setDialog({ plan, readOnly: true })}
            onEdit={() => setDialog({ plan, readOnly: false })}
            onStatus={async (status) => {
              try {
                await ax.patch(`/implementation-plans/${plan.id}`, { status });
                toast.success(`Plan ${status}`);
                await reload();
              } catch (e) {
                const detail = e?.response?.data?.detail;
                toast.error(typeof detail === "string" ? detail : "Failed to update status");
              }
            }}
          />
        ))}
      </section>

      <p className="text-[11px] text-[#9CA3AF] flex items-start gap-1">
        <Info className="w-3 h-3 mt-0.5" />
        USD is the base currency. Display currency and discount affect implementation
        presentation only — ROI calculations remain unaffected.
      </p>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Summary
// ---------------------------------------------------------------------------
function SummaryCards({ totals, currency, planCount }) {
  const safe = totals || {
    totalWeeks: 0,
    totalHours: 0,
    totalCostUsd: 0,
    totalRecurringUsd: 0,
    peakResources: 0,
  };
  const displayTotal = convert(safe.totalCostUsd, currency.fx_rate, currency.discount_pct);
  const recurringDisplay = convert(
    safe.totalRecurringUsd,
    currency.fx_rate,
    currency.discount_pct
  );
  const weeksLabel =
    safe.totalWeeks > 0
      ? `${safe.totalWeeks} weeks`
      : "0 weeks";
  return (
    <section
      className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3"
      data-testid="impl-summary-cards"
    >
      <SummaryTile
        icon={<Calendar className="w-3.5 h-3.5" />}
        label="Total Duration"
        primary={weeksLabel}
        secondary={`Across ${planCount} finalized plan${planCount === 1 ? "" : "s"}`}
        testId="impl-summary-duration"
      />
      <SummaryTile
        icon={<Clock className="w-3.5 h-3.5" />}
        label="Total Effort"
        primary={`${Math.round(safe.totalHours).toLocaleString()} hrs`}
        secondary="Finalized plans only"
        testId="impl-summary-effort"
      />
      <SummaryTile
        icon={<Users className="w-3.5 h-3.5" />}
        label="Resources"
        primary={`${safe.peakResources}`}
        secondary="Peak resource count across finalized plans"
        testId="impl-summary-team"
      />
      <SummaryTile
        icon={<DollarSign className="w-3.5 h-3.5" />}
        label="Total One-Time Investment"
        primary={fmtMoney(displayTotal, currency.display_currency)}
        secondary={`Base: ${fmtMoney(safe.totalCostUsd, "USD")}`}
        testId="impl-summary-onetime"
      />
      <SummaryTile
        icon={<DollarSign className="w-3.5 h-3.5" />}
        label="Total Annual Recurring"
        primary={fmtMoney(recurringDisplay, currency.display_currency)}
        secondary={`Base: ${fmtMoney(safe.totalRecurringUsd, "USD")}`}
        testId="impl-summary-recurring"
      />
    </section>
  );
}

function SummaryTile({ icon, label, primary, secondary, testId }) {
  return (
    <div
      data-testid={testId}
      className="bg-white border border-[#E5E7EB] rounded-sm p-3 shadow-sm"
    >
      <div className="text-[10px] uppercase tracking-wider text-[#9CA3AF] flex items-center gap-1 mb-1">
        {icon}
        {label}
      </div>
      <div className="text-lg font-bold text-[#111827] leading-tight">{primary}</div>
      <div className="text-[10px] text-[#6B7280] mt-0.5">{secondary}</div>
    </div>
  );
}

function CurrencyConverter({ currency, onChange, disabled }) {
  return (
    <div
      className="bg-white border border-[#E5E7EB] rounded-sm p-3 shadow-sm flex items-end gap-2 flex-wrap"
      data-testid="impl-currency-converter"
    >
      <div className="flex flex-col">
        <label className="text-[10px] uppercase tracking-wider text-[#6B7280] mb-1">
          Base
        </label>
        <span className="text-xs font-semibold text-[#111827] py-1.5">USD</span>
      </div>
      <div className="flex flex-col">
        <label className="text-[10px] uppercase tracking-wider text-[#6B7280] mb-1">
          Display
        </label>
        <Select
          value={currency.display_currency || "USD"}
          onValueChange={(v) => !disabled && onChange({ display_currency: v })}
          disabled={disabled}
        >
          <SelectTrigger className="rounded-sm text-xs h-8 w-24" data-testid="impl-currency-display">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {COMMON_CURRENCIES.map((c) => (
              <SelectItem key={c} value={c}>{c}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <div className="flex flex-col">
        <label className="text-[10px] uppercase tracking-wider text-[#6B7280] mb-1">FX Rate</label>
        <Input type="number" step="0.0001" value={currency.fx_rate ?? 1}
          onChange={(e) => onChange({ fx_rate: e.target.value })} disabled={disabled}
          className="rounded-sm text-xs h-8 w-24" data-testid="impl-currency-fx" />
      </div>
      <div className="flex flex-col">
        <label className="text-[10px] uppercase tracking-wider text-[#6B7280] mb-1">Discount %</label>
        <Input type="number" step="0.1" min="0" max="100" value={currency.discount_pct ?? 0}
          onChange={(e) => onChange({ discount_pct: e.target.value })} disabled={disabled}
          className="rounded-sm text-xs h-8 w-20" data-testid="impl-currency-discount" />
      </div>
    </div>
  );
}

function SummaryTable({ plans, finalizedPlans, currency, showDrafts, onToggleShowDrafts }) {
  const totalCostUsd = finalizedPlans.reduce((a, p) => a + (Number(p.total_cost_usd) || 0), 0);
  const totalDisplay = convert(totalCostUsd, currency.fx_rate, currency.discount_pct);
  const visiblePlans = showDrafts ? plans : plans.filter((p) => p.status === "finalized");
  return (
    <section className="bg-white border border-[#E5E7EB] rounded-sm" data-testid="impl-summary-table">
      <header className="px-4 py-3 border-b border-[#E5E7EB] flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3 flex-wrap">
          <h3 className="text-sm font-semibold text-[#111827]">Implementation Summary</h3>
          <label className="text-[11px] text-[#374151] flex items-center gap-1.5 cursor-pointer select-none" data-testid="impl-toggle-show-drafts-label">
            <input type="checkbox" checked={showDrafts} onChange={(e) => onToggleShowDrafts(e.target.checked)} className="accent-[#025979]" data-testid="impl-toggle-show-drafts" />
            Show Draft Plans
          </label>
        </div>
        <div className="flex items-center gap-4 text-[11px] text-[#6B7280]">
          <span>Finalized USD: <strong className="text-[#111827]">{fmtMoney(totalCostUsd, "USD")}</strong></span>
          <span>Finalized Display: <strong className="text-[#111827]">{fmtMoney(totalDisplay, currency.display_currency)}</strong></span>
          {Number(currency.discount_pct) > 0 ? (
            <span>Discount: <strong className="text-[#111827]">{Number(currency.discount_pct)}%</strong></span>
          ) : null}
        </div>
      </header>
      <div className="overflow-x-auto">
        <table className="w-full text-xs">
          <thead className="bg-[#F8FAFC] text-left text-[10px] uppercase tracking-wider text-[#6B7280]">
            <tr>
              <th className="px-3 py-2 font-semibold">Recommendation</th>
              <th className="px-3 py-2 font-semibold">Weeks</th>
              <th className="px-3 py-2 font-semibold">Effort</th>
              <th className="px-3 py-2 font-semibold text-right">Investment (USD)</th>
              <th className="px-3 py-2 font-semibold text-right">Discounted Cost</th>
              <th className="px-3 py-2 font-semibold">Status</th>
            </tr>
          </thead>
          <tbody>
            {visiblePlans.length === 0 ? (
              <tr>
                <td colSpan={6} className="px-3 py-6 text-center text-[#6B7280]" data-testid="impl-summary-table-empty">
                  {showDrafts ? "No implementation plans to display." : "No finalized implementation plans yet."}
                </td>
              </tr>
            ) : null}
            {visiblePlans.map((p) => {
              const display = convert(p.total_cost_usd, currency.fx_rate, currency.discount_pct);
              const weeks = Number(p.estimated_weeks) || 0;
              // Cards + summary display rule: round UP to next whole week.
              // Exact fractional weeks remain visible inside the editor.
              const weeksLabel = weeks
                ? `${Math.ceil(weeks)} week${Math.ceil(weeks) === 1 ? "" : "s"}`
                : "—";
              const isDraftRow = p.status !== "finalized";
              return (
                <tr key={p.id} className={`border-t border-[#F1F5F9] ${isDraftRow ? "text-[#6B7280]" : ""}`} data-testid={`impl-row-${p.rec_code}`}>
                  <td className="px-3 py-2 text-[#111827]">
                    <div className="font-semibold">{p.rec_code} — {p.rec_title}</div>
                    {p.rec_type ? (
                      <span className="inline-block mt-1 mr-1 px-1.5 py-0.5 text-[10px] font-semibold border rounded-sm bg-[#F0F9FF] text-[#075985] border-[#BAE6FD]">
                        {p.rec_type} · {p.rec_complexity || "—"}
                      </span>
                    ) : null}
                    {p.is_strategic ? (
                      <span className="inline-block mt-1 px-1.5 py-0.5 text-[10px] font-semibold border rounded-sm bg-[#FEF3C7] text-[#92400E] border-[#FDE68A]">Strategic</span>
                    ) : null}
                    {(p.linked_workstream_codes || []).length || (p.linked_finding_codes || []).length ? (
                      <div className="mt-1 text-[10px] text-[#6B7280]" data-testid={`impl-row-linkage-${p.rec_code}`}>
                        {(p.linked_workstream_codes || []).length ? (
                          <span><span className="font-semibold text-[#025979]">WS:</span> {(p.linked_workstream_codes || []).join(", ")}</span>
                        ) : null}
                        {(p.linked_workstream_codes || []).length && (p.linked_finding_codes || []).length ? " · " : ""}
                        {(p.linked_finding_codes || []).length ? (
                          <span><span className="font-semibold text-[#7C3AED]">Findings:</span> {(p.linked_finding_codes || []).join(", ")}</span>
                        ) : null}
                      </div>
                    ) : null}
                  </td>
                  <td className="px-3 py-2 whitespace-nowrap">{weeksLabel}</td>
                  <td className="px-3 py-2 whitespace-nowrap">{Math.round(Number(p.total_effort_hours) || 0).toLocaleString()} hrs</td>
                  <td className="px-3 py-2 text-right whitespace-nowrap">{fmtMoney(p.total_cost_usd, "USD")}</td>
                  <td className="px-3 py-2 text-right whitespace-nowrap">{fmtMoney(display, currency.display_currency)}</td>
                  <td className="px-3 py-2">
                    <span className={`inline-flex items-center px-1.5 py-0.5 text-[10px] font-semibold border rounded-sm capitalize ${STATUS_CHIP[p.status] || STATUS_CHIP.draft}`}>{p.status}</span>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </section>
  );
}

function PlanCard({ plan, currency, isFinalized, onView, onEdit, onStatus }) {
  const display = convert(plan.total_cost_usd, currency.fx_rate, currency.discount_pct);
  const weeks = Number(plan.estimated_weeks) || 0;
  // Card display rule: round UP to next whole week (1.0 → 1, 1.2 → 2, 2.1 → 3).
  // Editor still shows the exact fractional weeks via SummaryTile.
  const weeksCeil = weeks ? Math.ceil(weeks) : 0;
  const weeksLabel = weeksCeil
    ? `${weeksCeil} week${weeksCeil === 1 ? "" : "s"}`
    : "Timeline TBD";
  // Active resource count — blanks/deleted rows excluded.
  const planResourceCount = (plan.resources || []).filter(
    (r) => r && (r.role || "").toString().trim() !== "",
  ).length;
  const overrides = new Set(plan.override_fields || []);
  const isOverride = overrides.has("selected_template_id") && plan.selected_template_id !== plan.suggested_template_id;
  return (
    <article className="bg-white border border-[#E5E7EB] rounded-sm hover:border-[#94A3B8] transition" data-testid={`impl-card-${plan.rec_code}`}>
      <header className="px-4 py-3 border-b border-[#E5E7EB]">
        <div className="flex items-center gap-2 mb-1 flex-wrap">
          <span className="text-[10px] font-bold tracking-wider text-[#025979]">{plan.rec_code}</span>
          {plan.is_strategic ? (
            <span className="inline-block px-1.5 py-0.5 text-[10px] font-semibold border rounded-sm bg-[#F0F9FF] text-[#075985] border-[#BAE6FD]">Strategic</span>
          ) : null}
          {plan.is_strategic && plan.rec_type ? (
            <span className="inline-block px-1.5 py-0.5 text-[10px] font-semibold border rounded-sm bg-[#F1F5F9] text-[#475569] border-[#CBD5E1]">{plan.rec_type} · {plan.rec_complexity || "—"}</span>
          ) : null}
          {!plan.is_strategic ? (
            <span className="inline-block px-1.5 py-0.5 text-[10px] font-semibold border rounded-sm bg-[#F0F7FA] text-[#025979] border-[#BAE6FD]" data-testid={`impl-card-basis-${plan.rec_code}`}>
              {(plan.finding_count || (plan.finding_sizing || []).length)} finding{(plan.finding_count || (plan.finding_sizing || []).length) === 1 ? "" : "s"}
            </span>
          ) : null}
          {!plan.is_strategic && plan.sizing_incomplete ? (
            <span className="inline-block px-1.5 py-0.5 text-[10px] font-semibold border rounded-sm bg-[#FEF2F2] text-[#B91C1C] border-[#FECACA]" data-testid={`impl-card-incomplete-${plan.rec_code}`} title="Estimate incomplete — one or more findings are Unclassified">Estimate incomplete</span>
          ) : null}
          <span className={`inline-flex items-center px-1.5 py-0.5 text-[10px] font-semibold border rounded-sm capitalize ${STATUS_CHIP[plan.status] || STATUS_CHIP.draft}`}>{plan.status}</span>
          {isOverride ? (
            <span className="inline-block px-1.5 py-0.5 text-[10px] font-semibold border rounded-sm bg-[#FFF7ED] text-[#C2410C] border-[#FED7AA]">Consultant Override</span>
          ) : null}
        </div>
        <h3 className="text-sm font-semibold text-[#111827]" style={{ fontFamily: "Chivo, sans-serif" }}>{plan.rec_title}</h3>
        {plan.selected_template_title ? (
          <p className="text-[11px] text-[#6B7280] mt-0.5">
            {isOverride ? "Override Template: " : "Suggested Template: "}
            <span className="font-semibold text-[#374151]">{plan.selected_template_title}</span>
          </p>
        ) : null}
      </header>
      <div className="px-4 py-3 grid grid-cols-2 md:grid-cols-4 gap-3 text-xs">
        <Stat icon={<Calendar className="w-3 h-3" />} label="Estimated Timeline" value={weeksLabel} />
        <Stat icon={<DollarSign className="w-3 h-3" />} label="Total Investment" value={fmtMoney(plan.total_cost_usd, "USD")}
          secondary={currency.display_currency !== "USD" || Number(currency.discount_pct) > 0 ? `≈ ${fmtMoney(display, currency.display_currency)}` : null} />
        <Stat icon={<Clock className="w-3 h-3" />} label="Total Effort" value={`${Math.round(Number(plan.total_effort_hours) || 0).toLocaleString()} hrs`}
          secondary={null} />
        <Stat icon={<Users className="w-3 h-3" />} label="Resources" value={`${planResourceCount}`}
          secondary={planResourceCount === 1 ? "role" : "roles"} />
      </div>
      <PlanLinkage plan={plan} />
      {!plan.is_strategic && (plan.finding_sizing || []).length ? (
        <div className="px-4 pb-2" data-testid={`impl-card-basis-list-${plan.rec_code}`}>
          <div className="text-[10px] uppercase tracking-wider text-[#9CA3AF] mb-1">Implementation Basis</div>
          <div className="border border-[#E5E7EB] rounded-sm divide-y divide-[#F1F5F9]">
            {(plan.finding_sizing || []).map((f) => (
              <div key={f.finding_id} className="flex items-center gap-2 px-2 py-1 text-[11px]">
                <span className="font-bold tracking-wider text-[#025979] bg-[#F0F7FA] border border-[#BAE6FD] px-1.5 py-0.5 rounded-sm">{f.finding_code}</span>
                {f.classified ? (
                  <span className="text-[#374151]">{f.wricef_type} · {f.complexity}</span>
                ) : (
                  <span className="text-[#B91C1C] italic">Unclassified</span>
                )}
              </div>
            ))}
          </div>
        </div>
      ) : null}
      <footer className="px-4 py-2 border-t border-[#E5E7EB] flex items-center justify-end gap-2 flex-wrap">
        <Button type="button" variant="ghost" onClick={onView} className="rounded-sm text-xs h-7" data-testid={`impl-card-view-${plan.rec_code}`}>
          <Eye className="w-3 h-3 mr-1" /> View Details
        </Button>
        {!isFinalized ? (
          <>
            <Button type="button" variant="ghost" onClick={onEdit} className="rounded-sm text-xs h-7" data-testid={`impl-card-edit-${plan.rec_code}`}>
              <Pencil className="w-3 h-3 mr-1" /> Edit
            </Button>
            {plan.status !== "finalized" ? (
              <Button type="button" onClick={() => onStatus("finalized")} disabled={!plan.is_strategic && plan.sizing_incomplete} title={!plan.is_strategic && plan.sizing_incomplete ? "Classify all findings before finalizing" : undefined} className="rounded-sm text-xs h-7 bg-[#025979] hover:bg-[#03738C] text-white disabled:opacity-40" data-testid={`impl-card-finalize-${plan.rec_code}`}>
                <CheckCircle2 className="w-3 h-3 mr-1" /> Finalize
              </Button>
            ) : (
              <Button type="button" variant="ghost" onClick={() => onStatus("draft")} className="rounded-sm text-xs h-7" data-testid={`impl-card-reopen-${plan.rec_code}`}>Reopen</Button>
            )}
            {plan.status !== "archived" ? (
              <Button type="button" variant="ghost" onClick={() => onStatus("archived")} className="rounded-sm text-xs h-7 text-[#B91C1C] hover:text-[#7F1D1D]" data-testid={`impl-card-archive-${plan.rec_code}`}>
                <Archive className="w-3 h-3 mr-1" /> Archive
              </Button>
            ) : null}
          </>
        ) : null}
      </footer>
    </article>
  );
}

function Stat({ icon, label, value, secondary }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wider text-[#9CA3AF] flex items-center gap-1 mb-0.5">{icon}{label}</div>
      <div className="text-sm font-semibold text-[#111827]">{value}</div>
      {secondary ? <div className="text-[10px] text-[#6B7280] mt-0.5">{secondary}</div> : null}
    </div>
  );
}

// Traceability strip — the workstream IDs + finding IDs a plan's
// recommendation descends from. Resolved server-side on every read.
// Finding pills expose a hover tooltip with the finding title + priority.
function PlanLinkage({ plan }) {
  const wsCodes = plan.linked_workstream_codes || [];
  const findingCodes = plan.linked_finding_codes || [];
  const findings = plan.linked_findings || [];
  if (!wsCodes.length && !findingCodes.length) return null;
  return (
    <div
      className="px-4 pb-3 space-y-2"
      data-testid={`impl-card-linkage-${plan.rec_code}`}
    >
      {wsCodes.length ? (
        <CodePills
          label="Workstreams"
          codes={wsCodes}
          tone="ws"
          testId={`impl-card-ws-${plan.rec_code}`}
        />
      ) : null}
      {findingCodes.length ? (
        <FindingPills
          findings={findings.length ? findings : findingCodes.map((c) => ({ code: c }))}
          testId={`impl-card-findings-${plan.rec_code}`}
          recCode={plan.rec_code}
        />
      ) : null}
    </div>
  );
}

function CodePills({ label, codes, tone, testId }) {
  const toneCls =
    tone === "finding"
      ? "text-[#7C3AED] bg-[#F5F3FF] border-[#DDD6FE]"
      : "text-[#025979] bg-[#F0F7FA] border-[#BAE6FD]";
  return (
    <div className="flex items-start gap-2" data-testid={testId}>
      <span className="text-[10px] uppercase tracking-wider text-[#9CA3AF] shrink-0 mt-0.5 w-20">
        {label}
      </span>
      <div className="flex flex-wrap gap-1">
        {codes.map((c) => (
          <span
            key={c}
            className={`text-[10px] font-bold tracking-wider px-1.5 py-0.5 rounded-sm border ${toneCls}`}
          >
            {c}
          </span>
        ))}
      </div>
    </div>
  );
}

const PRIORITY_DOT = {
  critical: "bg-[#DC2626]",
  high: "bg-[#EA580C]",
  medium: "bg-[#CA8A04]",
  low: "bg-[#059669]",
};

// Finding pills with a hover tooltip surfacing the finding title + priority
// so consultants get context without leaving the Implementation tab.
function FindingPills({ findings, testId, recCode }) {
  return (
    <div className="flex items-start gap-2" data-testid={testId}>
      <span className="text-[10px] uppercase tracking-wider text-[#9CA3AF] shrink-0 mt-0.5 w-20">
        Findings
      </span>
      <TooltipProvider delayDuration={100}>
        <div className="flex flex-wrap gap-1">
          {findings.map((f) => {
            const priority = (f.priority || "").toLowerCase();
            const hasDetail = Boolean(f.title || f.priority);
            const pill = (
              <span
                className={`text-[10px] font-bold tracking-wider px-1.5 py-0.5 rounded-sm border text-[#7C3AED] bg-[#F5F3FF] border-[#DDD6FE] ${
                  hasDetail ? "cursor-help" : ""
                }`}
                data-testid={`impl-finding-pill-${recCode}-${f.code}`}
              >
                {f.code}
              </span>
            );
            if (!hasDetail) return <span key={f.code}>{pill}</span>;
            return (
              <Tooltip key={f.code}>
                <TooltipTrigger asChild>{pill}</TooltipTrigger>
                <TooltipContent
                  className="max-w-xs bg-[#0F172A] text-white border border-[#1E293B]"
                  data-testid={`impl-finding-tooltip-${recCode}-${f.code}`}
                >
                  <div className="space-y-1">
                    <div className="flex items-center gap-1.5">
                      <span className="text-[10px] font-bold tracking-wider text-[#C4B5FD]">
                        {f.code}
                      </span>
                      {priority ? (
                        <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider text-[#CBD5E1]">
                          <span
                            className={`w-1.5 h-1.5 rounded-full ${
                              PRIORITY_DOT[priority] || "bg-[#94A3B8]"
                            }`}
                          />
                          {priority}
                        </span>
                      ) : null}
                    </div>
                    {f.title ? (
                      <p className="text-xs leading-snug text-white">{f.title}</p>
                    ) : null}
                  </div>
                </TooltipContent>
              </Tooltip>
            );
          })}
        </div>
      </TooltipProvider>
    </div>
  );
}

// ===========================================================================
// PlanEditor — full-page SmartEvaluate-style implementation edit screen.
// Replaces the legacy modal popup. Uses the same 4-section layout
// (Template / Phases / Resources / Weekly Effort Matrix / Cost Model) and
// the same totals/cost math as SmartEvaluate.
// ===========================================================================
function PlanEditor({ plan, readOnly, templates, onClose, onSaved }) {
  const [selectedTemplateId, setSelectedTemplateId] = useState(plan.selected_template_id || "");
  const [phases, setPhases] = useState(plan.phases || []);
  const [resources, setResources] = useState(plan.resources || []);
  const [matrix, setMatrix] = useState(() => normaliseMatrix(plan.weekly_effort_matrix, plan.phases, plan.resources));
  const [costModel, setCostModel] = useState(() => ({
    hardware_usd: 0, integration_usd: 0, internal_business_effort_usd: 0,
    ams_annual_usd: 0, recurring_support_annual_usd: 0,
    ...(plan.cost_model || {}),
  }));
  const [saving, setSaving] = useState(false);

  const templateChanged = selectedTemplateId !== (plan.selected_template_id || "");

  // Selected template metadata is needed earlier so the WRICEF-weeks formula
  // can branch on slug. (selectedTemplate is also used further down for
  // matrix phase-group rendering.)
  const selectedTemplate = templates.find((t) => t.id === selectedTemplateId);
  const selectedSlug = (selectedTemplate?.slug || plan.selected_template_slug || "").toLowerCase();
  const isWricefMatrix = selectedSlug === "wricef_matrix";

  // Dropdown lists only templates matching this plan's purpose: strategic plans
  // → Implementation/Deployment templates; primary plans → Effort Sizing.
  const planPurpose = plan.is_strategic ? "implementation_deployment" : "effort_sizing";
  const visibleTemplates = templates.filter(
    (t) => !t.purpose || t.purpose === planPurpose,
  );

  const totalHours = matrixTotalHours(matrix);
  // Estimated Weeks:
  //   • WRICEF Matrix → MAX(resource effort hours) ÷ 40  (parallel team
  //     execution — the longest-loaded role drives wall-clock duration).
  //   • Big Bang / Pilot + Rollout → sum of phase durations.
  const wricefMaxHours = (() => {
    if (!resources?.length) return 0;
    const totals = resources.map((r) =>
      phases.reduce((a, p) => a + (Number(matrix[p.name]?.[r.role]) || 0), 0),
    );
    return Math.max(0, ...totals);
  })();
  const estimatedWeeks = isWricefMatrix
    ? Math.round((wricefMaxHours / 40) * 10) / 10
    : phaseTotalWeeks(phases);

  // Resource count (active, non-blank role rows) — replaces the previous
  // FTE display. Used in the editor header summary tile.
  const activeResourceCount = (resources || []).filter(
    (r) => r && (r.role || "").toString().trim() !== "",
  ).length;

  const services = useMemo(() => computeServicesCost(matrix, resources), [matrix, resources]);
  const totalOne = computeTotalOneTime(services, costModel);
  const totalAnnual = computeAnnualRecurring(costModel);

  // When phases/resources change, re-shape the matrix to match.
  const setPhasesAndReshape = (next) => {
    setPhases(next);
    setMatrix((prev) => normaliseMatrix(prev, next, resources));
  };
  const setResourcesAndReshape = (next) => {
    setResources(next);
    setMatrix((prev) => normaliseMatrix(prev, phases, next));
  };

  const applyTemplate = async (templateId) => {
    setSelectedTemplateId(templateId);
    if (!templateId) return;
    const t = templates.find((x) => x.id === templateId);
    if (!t) return;
    const seedPhases = (t.phases_detail || []).map((p) => ({
      name: p.name || "Phase",
      duration_weeks: Number(p.duration_weeks) || 0,
    }));
    const seedRes = (t.resources || []).map((r) => ({
      role: r.role || "Resource",
      delivery_type: "offshore",
      typical_hours: Number(r.typical_hours) || 0,
      onsite_rate_usd_per_hr: Number(r.onsite_rate_usd_per_hr) || 0,
      offshore_rate_usd_per_hr: Number(r.offshore_rate_usd_per_hr) || 0,
    }));
    setPhases(seedPhases);
    setResources(seedRes);
    let seededMatrix = normaliseMatrix(t.effort_matrix || {}, seedPhases, seedRes);

    // WRICEF Matrix Template: hydrate Functional / Technical / Team Lead
    // hours from the live WRICEF Efforts Card keyed by rec type+complexity.
    // Without this, the WRICEF matrix renders with all zeros until the user
    // saves and the server re-seeds — making the editor look broken.
    if ((t.slug || "").toLowerCase() === "wricef_matrix" && seedPhases.length === 1) {
      try {
        const r = await ax.get("/wricef-efforts-card");
        const recType = plan.rec_type || "Config";
        const recComplexity = plan.rec_complexity || "Medium";
        const card = (r.data && r.data.matrix) || {};
        const row = (card[recType] || card.Config || {})[recComplexity] || (card[recType] || {}).Medium || {};
        const fh = Number(row.functional) || 0;
        const th = Number(row.technical) || 0;
        const phaseName = seedPhases[0].name;
        const fnRole = seedRes.find((rr) => /functional/i.test(rr.role || ""))?.role;
        const techRole = seedRes.find((rr) => /technical/i.test(rr.role || ""))?.role;
        const teamLeadRole = seedRes.find((rr) => /(team\s*lead|lead|manager)/i.test(rr.role || "") && !/(functional|technical)/i.test(rr.role || ""))?.role;
        if (!seededMatrix[phaseName]) seededMatrix[phaseName] = {};
        if (fnRole) seededMatrix[phaseName][fnRole] = fh;
        if (techRole) seededMatrix[phaseName][techRole] = th;
        if (teamLeadRole) seededMatrix[phaseName][teamLeadRole] = Math.ceil(0.1 * (fh + th));
      } catch (e) {
        // If the lookup fails we leave the zero-filled matrix and rely on
        // the server-side seeder running on save.
      }
    }
    setMatrix(seededMatrix);
    setCostModel({
      hardware_usd: 0, integration_usd: 0, internal_business_effort_usd: 0,
      ams_annual_usd: 0, recurring_support_annual_usd: 0,
      ...(t.default_cost_model || {}),
    });
  };

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        phases,
        resources,
        weekly_effort_matrix: matrix,
        cost_model: costModel,
      };
      if (templateChanged) payload.selected_template_id = selectedTemplateId;
      await ax.patch(`/implementation-plans/${plan.id}`, payload);
      toast.success("Implementation plan saved");
      await onSaved();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Failed to save plan");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-5 max-w-7xl mx-auto" data-testid="impl-edit-page">
      <button
        type="button"
        onClick={onClose}
        className="text-xs text-[#025979] hover:text-[#03738C] flex items-center gap-1"
        data-testid="impl-edit-back"
      >
        ← Back to Implementation
      </button>

      <header className="bg-white border border-[#E5E7EB] rounded-sm px-5 py-4 flex flex-wrap items-start justify-between gap-4">
        <div className="min-w-0">
          <div className="flex items-center gap-2 flex-wrap mb-1">
            <span className="text-[10px] font-bold tracking-wider text-[#025979]">
              {plan.rec_code}
            </span>
            {plan.is_strategic ? (
              <span className="inline-block px-1.5 py-0.5 text-[10px] font-semibold border rounded-sm bg-[#FEF3C7] text-[#92400E] border-[#FDE68A]">
                Strategic
              </span>
            ) : null}
            {plan.is_strategic && plan.rec_type ? (
              <span className="inline-block px-1.5 py-0.5 text-[10px] font-semibold border rounded-sm bg-[#F0F9FF] text-[#075985] border-[#BAE6FD]">
                {plan.rec_type} · {plan.rec_complexity || "—"}
              </span>
            ) : null}
            <span className={`inline-flex items-center px-1.5 py-0.5 text-[10px] font-semibold border rounded-sm capitalize ${STATUS_CHIP[plan.status] || STATUS_CHIP.draft}`}>
              {plan.status}
            </span>
          </div>
          <h2 className="text-lg font-bold text-[#111827]" style={{ fontFamily: "Chivo" }}>
            {plan.rec_title}
          </h2>
          <p className="text-[11px] text-[#6B7280] mt-1">
            {readOnly
              ? "Review the implementation plan."
              : "Adjust phases, resources, weekly effort, and cost model. Totals recalculate instantly. Templates are sourced from SmartEvaluate → Admin → Implementation Model."}
          </p>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {!readOnly ? (
            <Button
              variant="ghost"
              onClick={onClose}
              className="rounded-sm text-xs"
              data-testid="impl-edit-cancel"
            >
              Cancel
            </Button>
          ) : (
            <Button
              variant="ghost"
              onClick={onClose}
              className="rounded-sm text-xs"
              data-testid="impl-edit-close"
            >
              Close
            </Button>
          )}
          {!readOnly ? (
            <Button
              onClick={save}
              disabled={saving}
              className="bg-[#025979] hover:bg-[#03738C] text-white rounded-sm text-xs"
              data-testid="impl-edit-save"
            >
              {saving ? "Saving…" : "Save Implementation Inputs"}
            </Button>
          ) : null}
        </div>
      </header>

      <section className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="impl-edit-summary-strip">
        <SummaryTile
          icon={<Calendar className="w-3.5 h-3.5" />}
          label="Estimated Duration"
          primary={`${estimatedWeeks % 1 === 0 ? estimatedWeeks : estimatedWeeks.toFixed(1)} weeks`}
          secondary={isWricefMatrix ? "Max resource effort ÷ 40" : "Sum of phase durations"}
          testId="impl-edit-summary-weeks"
        />
        <SummaryTile
          icon={<Clock className="w-3.5 h-3.5" />}
          label="Total Effort"
          primary={`${Math.round(totalHours).toLocaleString()} hrs`}
          secondary={`${activeResourceCount} Resource${activeResourceCount === 1 ? "" : "s"}`}
          testId="impl-edit-summary-effort"
        />
        <SummaryTile
          icon={<DollarSign className="w-3.5 h-3.5" />}
          label="One-Time Investment"
          primary={fmtMoney(totalOne, "USD")}
          secondary="Services + Hardware + Integration + Internal"
          testId="impl-edit-summary-onetime"
        />
        <SummaryTile
          icon={<DollarSign className="w-3.5 h-3.5" />}
          label="Annual Recurring"
          primary={fmtMoney(totalAnnual, "USD")}
          secondary="AMS + Recurring Support"
          testId="impl-edit-summary-recurring"
        />
      </section>

      {!plan.is_strategic && (plan.finding_sizing || []).length ? (
        <section className="bg-white border border-[#E5E7EB] rounded-sm" data-testid="impl-edit-finding-sizing">
          <div className="px-5 py-3 border-b border-[#E5E7EB] flex items-center gap-2">
            <h3 className="text-[11px] font-semibold tracking-[0.16em] uppercase text-[#025979]">Implementation Basis — Finding Sizing</h3>
            {plan.sizing_incomplete ? (
              <span className="inline-block px-1.5 py-0.5 text-[10px] font-semibold border rounded-sm bg-[#FEF2F2] text-[#B91C1C] border-[#FECACA]">Incomplete — classify all findings</span>
            ) : null}
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead className="bg-[#F8FAFC] text-[#6B7280] text-left">
                <tr>
                  <th className="px-4 py-2 font-semibold">Finding</th>
                  <th className="px-4 py-2 font-semibold">WRICEF Type</th>
                  <th className="px-4 py-2 font-semibold">Complexity</th>
                  <th className="px-4 py-2 font-semibold text-right">Functional Hrs</th>
                  <th className="px-4 py-2 font-semibold text-right">Technical Hrs</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[#F1F5F9]">
                {(plan.finding_sizing || []).map((f) => (
                  <tr key={f.finding_id}>
                    <td className="px-4 py-2">
                      <span className="font-bold tracking-wider text-[#025979]">{f.finding_code}</span>
                      {f.finding_title ? <span className="text-[#6B7280]"> — {f.finding_title}</span> : null}
                    </td>
                    <td className="px-4 py-2 text-[#374151]">{f.classified ? f.wricef_type : <span className="text-[#B91C1C] italic">Unclassified</span>}</td>
                    <td className="px-4 py-2 text-[#374151]">{f.classified ? f.complexity : <span className="text-[#B91C1C] italic">Unclassified</span>}</td>
                    <td className="px-4 py-2 text-right text-[#374151]">{f.classified ? Math.round(f.functional_hours).toLocaleString() : "—"}</td>
                    <td className="px-4 py-2 text-right text-[#374151]">{f.classified ? Math.round(f.technical_hours).toLocaleString() : "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="px-5 py-2 text-[10px] text-[#9CA3AF]">Sized from finding-level WRICEF Type × Complexity (Admin WRICEF Efforts Card). Role totals, Team Lead (ceil 10%) and cost are below.</p>
        </section>
      ) : null}

      <div className="bg-white border border-[#E5E7EB] rounded-sm">
        <div className="px-5 py-4 border-b border-[#E5E7EB]">
          <Section label="Template">
            {readOnly ? (
              <p className="text-sm text-[#111827] font-semibold">
                {plan.selected_template_title || "—"}
              </p>
            ) : (
              <>
                <Select value={selectedTemplateId || "__none"} onValueChange={(v) => applyTemplate(v === "__none" ? "" : v)}>
                  <SelectTrigger className="rounded-sm text-xs" data-testid="impl-edit-template"><SelectValue placeholder="Select template" /></SelectTrigger>
                  <SelectContent>
                    {visibleTemplates.map((t) => (
                      <SelectItem key={t.id} value={t.id}>{t.title}{t.is_default ? " (Default)" : t.id === plan.suggested_template_id ? " (Suggested)" : ""}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-[10px] text-[#6B7280] mt-1">
                  Switching template hard-resets phases, resources, effort matrix, and cost model to the template's defaults. Templates are sourced from Admin → Implementation Templates ({isWricefMatrix ? "Effort Sizing" : "SmartImprove · Implementation / Deployment"}).
                </p>
              </>
            )}
          </Section>
        </div>

        {!isWricefMatrix ? (
          <div className="px-5 py-4 border-b border-[#E5E7EB]">
            <Section label="Implementation Stages">
              <PhasesTable phases={phases} readOnly={readOnly} onChange={setPhasesAndReshape} totalWeeks={estimatedWeeks} />
            </Section>
          </div>
        ) : null}

        <div className="px-5 py-4 border-b border-[#E5E7EB]">
          <Section label="Resources">
            <ResourcesTable resources={resources} readOnly={readOnly} onChange={setResourcesAndReshape} />
          </Section>
        </div>

        <div className="px-5 py-4 border-b border-[#E5E7EB]">
          <Section label={isWricefMatrix ? "Effort Matrix" : "Weekly Effort Matrix"}>
            <EffortMatrixTable
              phases={phases}
              phaseGroups={selectedTemplate?.phase_groups || []}
              resources={resources}
              matrix={matrix}
              readOnly={readOnly}
              onChange={setMatrix}
              totalHours={totalHours}
              isWricefMatrix={isWricefMatrix}
            />
          </Section>
        </div>

        <div className="px-5 py-4">
          <Section label="Cost Model">
            <CostModelTable
              cm={costModel}
              services={services}
              totalOne={totalOne}
              totalAnnual={totalAnnual}
              readOnly={readOnly}
              onChange={setCostModel}
            />
          </Section>
        </div>
      </div>

      {!readOnly ? (
        <div className="flex items-center justify-end gap-2 sticky bottom-3 z-10 bg-white border border-[#E5E7EB] rounded-sm px-3 py-2 shadow-md">
          <Button
            variant="ghost"
            onClick={onClose}
            className="rounded-sm text-xs"
            data-testid="impl-edit-cancel-bottom"
          >
            Cancel
          </Button>
          <Button
            onClick={save}
            disabled={saving}
            className="bg-[#025979] hover:bg-[#03738C] text-white rounded-sm text-xs"
            data-testid="impl-edit-save-bottom"
          >
            {saving ? "Saving…" : "Save Implementation Inputs"}
          </Button>
        </div>
      ) : null}
    </div>
  );
}

function normaliseMatrix(matrix, phases, resources) {
  const next = {};
  (phases || []).forEach((p) => {
    const name = p.name || "Phase";
    next[name] = {};
    (resources || []).forEach((r) => {
      const role = r.role || "Resource";
      const existing = (matrix || {})[name]?.[role];
      next[name][role] = Number(existing) || 0;
    });
  });
  return next;
}

function Section({ label, children }) {
  return (
    <div>
      <h4 className="text-[11px] font-semibold text-[#025979] uppercase tracking-[0.15em] mb-2">{label}</h4>
      {children}
    </div>
  );
}

function PhasesTable({ phases, readOnly, onChange, totalWeeks }) {
  const update = (i, patch) => {
    const next = [...phases];
    next[i] = { ...next[i], ...patch };
    onChange(next);
  };
  const remove = (i) => onChange(phases.filter((_, idx) => idx !== i));
  const add = () => onChange([...phases, { name: "", duration_weeks: 0 }]);
  return (
    <div className="bg-white border border-[#E5E7EB] rounded-sm overflow-hidden" data-testid="impl-phases-table">
      <table className="w-full text-xs">
        <thead className="bg-[#F8FAFC] text-left text-[10px] uppercase tracking-wider text-[#6B7280]">
          <tr>
            <th className="px-3 py-2 font-semibold">Phase</th>
            <th className="px-3 py-2 font-semibold w-40">Duration (Weeks)</th>
            {!readOnly ? <th className="px-3 py-2 w-12" /> : null}
          </tr>
        </thead>
        <tbody>
          {phases.length === 0 ? (
            <tr><td colSpan={readOnly ? 2 : 3} className="px-3 py-3 text-[#6B7280] text-center">No phases yet.</td></tr>
          ) : null}
          {phases.map((p, i) => (
            <tr key={i} className="border-t border-[#F1F5F9]">
              <td className="px-3 py-2">
                {readOnly ? <span className="text-[#111827]">{p.name || "—"}</span> :
                  <Input value={p.name || ""} onChange={(e) => update(i, { name: e.target.value })} placeholder="Phase name" className="rounded-sm text-xs h-8" data-testid={`phase-name-${i}`} />}
              </td>
              <td className="px-3 py-2">
                {readOnly ? <span className="text-[#111827]">{p.duration_weeks || 0}</span> :
                  <Input type="number" step="0.5" min="0" value={p.duration_weeks ?? 0} onChange={(e) => update(i, { duration_weeks: Number(e.target.value) || 0 })} className="rounded-sm text-xs h-8" data-testid={`phase-weeks-${i}`} />}
              </td>
              {!readOnly ? (
                <td className="px-3 py-2 text-right">
                  <button type="button" onClick={() => remove(i)} className="text-[#B91C1C] text-xs" aria-label="Remove phase">✕</button>
                </td>
              ) : null}
            </tr>
          ))}
          <tr className="border-t-2 border-[#E5E7EB] bg-[#F8FAFC]">
            <td className="px-3 py-2 text-[11px] font-semibold text-[#111827]">Total Estimated Weeks</td>
            <td className="px-3 py-2 text-[11px] font-bold text-[#111827]" data-testid="phase-total-weeks">{totalWeeks % 1 === 0 ? totalWeeks : totalWeeks.toFixed(1)} weeks</td>
            {!readOnly ? <td /> : null}
          </tr>
        </tbody>
      </table>
      {!readOnly ? (
        <div className="px-3 py-2 border-t border-[#F1F5F9]">
          <button type="button" onClick={add} className="text-xs font-semibold text-[#025979] hover:text-[#03738C]" data-testid="phase-add-btn">+ Add Phase</button>
        </div>
      ) : null}
    </div>
  );
}

function ResourcesTable({ resources, readOnly, onChange }) {
  const update = (i, patch) => {
    const next = [...resources];
    next[i] = { ...next[i], ...patch };
    onChange(next);
  };
  const remove = (i) => onChange(resources.filter((_, idx) => idx !== i));
  const add = () => onChange([...resources, { role: "", delivery_type: "offshore", typical_hours: 0, onsite_rate_usd_per_hr: 0, offshore_rate_usd_per_hr: 0 }]);
  return (
    <div className="bg-white border border-[#E5E7EB] rounded-sm overflow-hidden" data-testid="impl-resources-table">
      <table className="w-full text-xs">
        <thead className="bg-[#F8FAFC] text-left text-[10px] uppercase tracking-wider text-[#6B7280]">
          <tr>
            <th className="px-3 py-2 font-semibold">Role</th>
            <th className="px-3 py-2 font-semibold w-32">Delivery</th>
            <th className="px-3 py-2 font-semibold w-28">Onsite Rate</th>
            <th className="px-3 py-2 font-semibold w-28">Offshore Rate</th>
            <th className="px-3 py-2 font-semibold w-28">Blended Rate</th>
            {!readOnly ? <th className="px-3 py-2 w-12" /> : null}
          </tr>
        </thead>
        <tbody>
          {resources.length === 0 ? (
            <tr><td colSpan={readOnly ? 5 : 6} className="px-3 py-3 text-[#6B7280] text-center">No resources yet.</td></tr>
          ) : null}
          {resources.map((r, i) => {
            const delivery = (r.delivery_type || "offshore").toLowerCase();
            const blended = resourceBlendedRate(r);
            return (
              <tr key={i} className="border-t border-[#F1F5F9]">
                <td className="px-3 py-2">
                  {readOnly ? <span className="text-[#111827]">{r.role || "—"}</span> :
                    <Input value={r.role || ""} onChange={(e) => update(i, { role: e.target.value })} placeholder="Role" className="rounded-sm text-xs h-8" data-testid={`resource-role-${i}`} />}
                </td>
                <td className="px-3 py-2">
                  {readOnly ? <span className="text-[#111827] capitalize">{delivery}</span> :
                    <Select value={delivery} onValueChange={(v) => update(i, { delivery_type: v })}>
                      <SelectTrigger className="rounded-sm text-xs h-8" data-testid={`resource-delivery-${i}`}><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="onsite">Onsite</SelectItem>
                        <SelectItem value="offshore">Offshore</SelectItem>
                      </SelectContent>
                    </Select>}
                </td>
                <td className="px-3 py-2">
                  {readOnly ? <span className="text-[#111827]">{r.onsite_rate_usd_per_hr || 0}</span> :
                    <Input type="number" min="0" value={r.onsite_rate_usd_per_hr ?? 0} onChange={(e) => update(i, { onsite_rate_usd_per_hr: Number(e.target.value) || 0 })} className="rounded-sm text-xs h-8" data-testid={`resource-onsite-rate-${i}`} />}
                </td>
                <td className="px-3 py-2">
                  {readOnly ? <span className="text-[#111827]">{r.offshore_rate_usd_per_hr || 0}</span> :
                    <Input type="number" min="0" value={r.offshore_rate_usd_per_hr ?? 0} onChange={(e) => update(i, { offshore_rate_usd_per_hr: Number(e.target.value) || 0 })} className="rounded-sm text-xs h-8" data-testid={`resource-offshore-rate-${i}`} />}
                </td>
                <td className="px-3 py-2 font-semibold text-[#111827] whitespace-nowrap">{fmtMoney(blended, "USD")}/hr</td>
                {!readOnly ? (
                  <td className="px-3 py-2 text-right">
                    <button type="button" onClick={() => remove(i)} className="text-[#B91C1C] text-xs" aria-label="Remove resource">✕</button>
                  </td>
                ) : null}
              </tr>
            );
          })}
        </tbody>
      </table>
      {!readOnly ? (
        <div className="px-3 py-2 border-t border-[#F1F5F9]">
          <button type="button" onClick={add} className="text-xs font-semibold text-[#025979] hover:text-[#03738C]" data-testid="resource-add-btn">+ Add Resource</button>
        </div>
      ) : null}
    </div>
  );
}

function EffortMatrixTable({ phases, phaseGroups, resources, matrix, readOnly, onChange, totalHours, isWricefMatrix }) {
  const updateCell = (phaseName, role, value) => {
    const next = { ...matrix, [phaseName]: { ...(matrix[phaseName] || {}), [role]: Number(value) || 0 } };
    onChange(next);
  };
  if (!phases?.length || !resources?.length) {
    return <div className="text-xs text-[#6B7280] py-3">Add phases and resources to populate the effort matrix.</div>;
  }

  // -----------------------------------------------------------------------
  // WRICEF Matrix layout — flat Role table. No phase grid. Cost auto-derived.
  // -----------------------------------------------------------------------
  if (isWricefMatrix) {
    const phaseName = phases[0]?.name || "Delivery";
    const totalCost = resources.reduce((sum, r) => {
      const hrs = Number(matrix[phaseName]?.[r.role]) || 0;
      return sum + hrs * resourceBlendedRate(r);
    }, 0);
    return (
      <div className="bg-white border border-[#E5E7EB] rounded-sm overflow-x-auto" data-testid="impl-effort-matrix-wricef">
        <table className="w-full text-xs">
          <thead className="bg-[#F8FAFC] text-[10px] uppercase tracking-wider text-[#6B7280]">
            <tr>
              <th className="px-3 py-2 font-semibold text-left">Role</th>
              <th className="px-3 py-2 font-semibold text-right">Effective Rate</th>
              <th className="px-3 py-2 font-semibold text-right">Effort (Hours)</th>
              <th className="px-3 py-2 font-semibold text-right">Cost (USD)</th>
            </tr>
          </thead>
          <tbody>
            {resources.map((r, ri) => {
              const hrs = Number(matrix[phaseName]?.[r.role]) || 0;
              const rate = resourceBlendedRate(r);
              const cost = hrs * rate;
              return (
                <tr key={r.role + ri} className="border-t border-[#F1F5F9]" data-testid={`wricef-row-${ri}`}>
                  <td className="px-3 py-2 font-semibold text-[#111827]">{r.role}</td>
                  <td className="px-3 py-2 text-right whitespace-nowrap text-[#111827]">{fmtMoney(rate, "USD")}/hr</td>
                  <td className="px-2 py-1 text-right">
                    {readOnly ? <span className="text-[#111827]">{hrs}</span> :
                      <Input type="number" min="0" step="1" value={hrs}
                        onChange={(e) => updateCell(phaseName, r.role, e.target.value)}
                        className="rounded-sm text-xs h-7 w-24 text-right ml-auto"
                        data-testid={`wricef-cell-hours-${ri}`} />}
                  </td>
                  <td className="px-3 py-2 text-right font-semibold text-[#111827] whitespace-nowrap" data-testid={`wricef-cell-cost-${ri}`}>
                    {fmtMoney(cost, "USD")}
                  </td>
                </tr>
              );
            })}
            <tr className="border-t-2 border-[#E5E7EB] bg-[#F8FAFC]">
              <td className="px-3 py-2 text-[11px] font-bold text-[#111827]" colSpan={2}>Totals</td>
              <td className="px-3 py-2 text-right text-[11px] font-bold text-[#111827]" data-testid="wricef-total-hours">
                {Math.round(totalHours)} hrs
              </td>
              <td className="px-3 py-2 text-right text-[11px] font-bold text-[#111827]" data-testid="wricef-total-cost">
                {fmtMoney(totalCost, "USD")}
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    );
  }

  // -----------------------------------------------------------------------
  // Big Bang / Pilot + Rollout — Resource ROWS × Phase COLUMNS, with the
  // phase columns grouped under the template's stage headers (Foundation /
  // Big Bang / Wave 1 / Hypercare …). Matches SmartEvaluate's matrix layout
  // 1:1 so consultants flipping between modules see an identical grid.
  // -----------------------------------------------------------------------
  // Build the column groupings. Phases not in any group fall into "Other"
  // (e.g. consultant-added custom phases). Each column carries its group
  // index so duplicate phase names across waves (e.g. Pilot.Build vs
  // Wave1.Build in Pilot + Rollout) get unique React keys.
  const phaseNames = phases.map((p) => p.name);
  const grouped = [];
  const claimed = new Set();
  for (const g of (phaseGroups || [])) {
    const cols = (g.phases || [])
      .map((p) => p.name)
      .filter((n) => phaseNames.includes(n));
    cols.forEach((n) => claimed.add(n));
    if (cols.length > 0) grouped.push({ name: g.name || "", phases: cols });
  }
  const remaining = phaseNames.filter((n) => !claimed.has(n));
  if (remaining.length > 0) grouped.push({ name: "Other", phases: remaining });
  // Flatten with group-qualified column ids so duplicate phase names across
  // waves don't collide on React keys.
  const orderedCols = grouped.flatMap((g, gi) =>
    g.phases.map((name, pi) => ({ id: `${gi}-${pi}-${name}`, name, group: gi })),
  );
  const orderedPhases = orderedCols.map((c) => c.name);

  const cellValue = (phaseName, role) => Number(matrix[phaseName]?.[role]) || 0;
  const rowTotal = (role) => orderedPhases.reduce((a, p) => a + cellValue(p, role), 0);
  const colTotal = (phaseName) => resources.reduce((a, r) => a + cellValue(phaseName, r.role), 0);

  return (
    <div className="bg-white border border-[#E5E7EB] rounded-sm overflow-x-auto" data-testid="impl-effort-matrix">
      <table className="w-full text-xs border-collapse">
        <thead className="bg-[#F8FAFC] text-[10px] uppercase tracking-wider text-[#6B7280]">
          {grouped.length > 0 ? (
            <tr>
              <th className="px-3 py-2 font-semibold text-left sticky left-0 bg-[#F8FAFC] z-10 border-r border-[#E5E7EB]">
                Resource
              </th>
              {grouped.map((g, gi) => (
                <th
                  key={`grp-${gi}-${g.name}`}
                  colSpan={g.phases.length}
                  className="px-3 py-1 font-bold text-center text-[#025979] border-b border-x border-[#BAE6FD] bg-[#E0F2FE]"
                  data-testid={`matrix-group-${gi}`}
                >
                  {g.name}
                </th>
              ))}
              <th rowSpan={2} className="px-3 py-2 font-semibold text-right border-l border-[#E5E7EB]">Row Total</th>
            </tr>
          ) : null}
          <tr>
            {grouped.length === 0 ? (
              <th className="px-3 py-2 font-semibold text-left sticky left-0 bg-[#F8FAFC] z-10 border-r border-[#E5E7EB]">
                Resource
              </th>
            ) : null}
            {orderedCols.map((c) => (
              <th key={`ph-${c.id}`} className="px-2 py-2 font-semibold text-right whitespace-nowrap">{c.name}</th>
            ))}
            {grouped.length === 0 ? (
              <th className="px-3 py-2 font-semibold text-right border-l border-[#E5E7EB]">Row Total</th>
            ) : null}
          </tr>
        </thead>
        <tbody>
          {resources.map((r, ri) => (
            <tr key={r.role + ri} className="border-t border-[#F1F5F9]" data-testid={`matrix-resource-row-${ri}`}>
              <td className="px-3 py-2 font-semibold text-[#111827] whitespace-nowrap sticky left-0 bg-white border-r border-[#E5E7EB]">
                {r.role}
              </td>
              {orderedCols.map((c, pi) => {
                const v = cellValue(c.name, r.role);
                return (
                  <td key={`${r.role}-${c.id}`} className="px-1 py-1 text-right">
                    {readOnly ? <span className="text-[#111827]">{v}</span> :
                      <Input type="number" min="0" step="1" value={v}
                        onChange={(e) => updateCell(c.name, r.role, e.target.value)}
                        className="rounded-sm text-xs h-7 w-16 text-right ml-auto"
                        data-testid={`matrix-cell-${ri}-${pi}`} />}
                  </td>
                );
              })}
              <td className="px-3 py-2 text-right font-semibold text-[#111827] border-l border-[#E5E7EB]">{Math.round(rowTotal(r.role))}</td>
            </tr>
          ))}
          <tr className="border-t-2 border-[#E5E7EB] bg-[#F8FAFC]">
            <td className="px-3 py-2 text-[11px] font-bold text-[#111827] sticky left-0 bg-[#F8FAFC] border-r border-[#E5E7EB]">
              Column Total
            </td>
            {orderedCols.map((c) => (
              <td key={`tot-${c.id}`} className="px-3 py-2 text-right text-[11px] font-bold text-[#111827]">{Math.round(colTotal(c.name))}</td>
            ))}
            <td className="px-3 py-2 text-right text-[11px] font-bold text-[#111827] border-l border-[#E5E7EB]" data-testid="matrix-total-hours">{Math.round(totalHours)} hrs</td>
          </tr>
        </tbody>
      </table>
    </div>
  );
}

function CostModelTable({ cm, services, totalOne, totalAnnual, readOnly, onChange }) {
  const update = (key, value) => onChange({ ...cm, [key]: Number(value) || 0 });
  return (
    <div className="bg-white border border-[#E5E7EB] rounded-sm overflow-hidden" data-testid="impl-cost-model">
      <table className="w-full text-xs">
        <thead className="bg-[#F8FAFC] text-left text-[10px] uppercase tracking-wider text-[#6B7280]">
          <tr>
            <th className="px-3 py-2 font-semibold">Cost Component</th>
            <th className="px-3 py-2 font-semibold text-right">Amount (USD)</th>
          </tr>
        </thead>
        <tbody>
          <tr className="border-t border-[#F1F5F9]">
            <td className="px-3 py-2 text-[#111827]">Implementation Services <span className="text-[10px] text-[#6B7280]">(matrix × rate)</span></td>
            <td className="px-3 py-2 text-right font-semibold text-[#111827]" data-testid="cm-services">{fmtMoney(services, "USD")}</td>
          </tr>
          {COST_MODEL_KEYS.map(({ key, label }) => (
            <tr key={key} className="border-t border-[#F1F5F9]">
              <td className="px-3 py-2 text-[#111827]">{label}</td>
              <td className="px-3 py-2 text-right">
                {readOnly ? <span className="text-[#111827]">{fmtMoney(cm[key] || 0, "USD")}</span> :
                  <Input type="number" min="0" step="100" value={cm[key] ?? 0}
                    onChange={(e) => update(key, e.target.value)}
                    className="rounded-sm text-xs h-8 text-right inline-block w-40"
                    data-testid={`cm-${key}`} />}
              </td>
            </tr>
          ))}
          <tr className="border-t-2 border-[#E5E7EB] bg-[#F8FAFC]">
            <td className="px-3 py-2 text-[11px] font-bold text-[#111827]">Total One-Time Investment</td>
            <td className="px-3 py-2 text-right text-[11px] font-bold text-[#111827]" data-testid="cm-total-one">{fmtMoney(totalOne, "USD")}</td>
          </tr>
          <tr className="bg-[#F8FAFC]">
            <td className="px-3 py-2 text-[11px] font-bold text-[#111827]">Total Annual Recurring</td>
            <td className="px-3 py-2 text-right text-[11px] font-bold text-[#111827]" data-testid="cm-total-annual">{fmtMoney(totalAnnual, "USD")}</td>
          </tr>
        </tbody>
      </table>
    </div>
  );
}

function EmptyState({ title, description }) {
  return (
    <div className="bg-white border border-dashed border-[#E5E7EB] rounded-sm py-16 px-6 text-center max-w-3xl mx-auto" data-testid="impl-empty-state">
      <Workflow className="w-8 h-8 text-[#94A3B8] mx-auto mb-3" />
      <h3 className="text-sm font-semibold text-[#111827]">{title}</h3>
      <p className="text-xs text-[#6B7280] mt-1">{description}</p>
    </div>
  );
}
